import $ from "jquery";
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { FormBuilderContext } from "context/FormBuilderCtx";
import {
  Grid,
  Icon,
  CircularProgress,
  TextField,
  Box,
  Container,
  FormHelperText,
  IconButton,
  Checkbox,
  Divider,
  Button,
} from "@mui/material";
import MDAutocomplete from "components/MDAutocomplete";
import * as Yup from "yup";
import "../../style/scss/FormBuilder/index.css";
import { getFormElements, getAllTemplateFormsApi } from "lib/Api/FormBuilder";
import { useNavigate } from "react-router-dom";
import MDLink from "components/MDLink";
import {
  DRAG_AREA_TEXT,
  CUSTOM_FIELD_NAME,
  NOT_TIME_FIELDS,
  SEARCH_INPUT_TYPES,
  CHILD_FORM_DEFAULT_COLUMNS,
  CHILD_FORM_MANDATORY_COLUMNS,
} from "utils/constants";
import { useFormik } from "formik";
import { VALIDATION_MESSAGES } from "utils/constants";
import { LoginFormContext } from "context/loginFormCtx";
import { createForm } from "lib/Api/FormBuilder";
import DraggableDialog from "components/draggagle-dialog";
import ViewFormPreview from "./ViewFormPreview";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
window.jQuery = $;
window.$ = $;

/**
 * XMLHttpRequest Interceptor for Form Builder Language File
 * 
 * PROBLEM:
 * The jQuery Form Builder library makes an unnecessary AJAX request to '/assets/lang/en-US.lang' 
 * on initialization. This request always results in a 404 error since the file doesn't exist,
 * but it still causes network traffic, console errors, and potentially slow page loads
 * (especially in staging/UAT environments where the 404 response can take 10-20+ seconds).
 * 
 * SOLUTION:
 * We intercept all XMLHttpRequest calls (the foundation of all AJAX requests) and:
 * 1. Identify requests for the language file
 * 2. Block those specific requests from actually hitting the network
 * 3. Simulate a 404 response that matches what would happen normally
 * 4. Let the form builder handle the 404 gracefully as it would anyway
 * 
 * BENEFITS:
 * - Eliminates unnecessary network requests
 * - Improves page load performance
 * - Reduces server load
 * - Maintains expected behavior without modifying the library
 */
if (window.XMLHttpRequest) {
  const originalXHROpen = window.XMLHttpRequest.prototype.open;
  const originalXHRSend = window.XMLHttpRequest.prototype.send;
  
  // Step 1: Override the open method to capture URL information
  window.XMLHttpRequest.prototype.open = function(method, url, async, user, password) {
    // Store URL and method for later use in the send method
    this._url = url;
    this._method = method;
    return originalXHROpen.apply(this, arguments);
  };
  
  // Step 2: Override the send method to intercept specific requests
  window.XMLHttpRequest.prototype.send = function(body) {
    // Check if this is the language file request we want to block
    if (this._url && this._url.includes('assets/lang/en-US.lang')) {
      
      // Step 3: Mock a 404 error response that matches what would happen normally
      // Set all the XHR properties that would be set in a real 404 response
      Object.defineProperty(this, 'readyState', { value: 4, writable: true }); // 4 = DONE
      Object.defineProperty(this, 'status', { value: 404, writable: true });   // 404 = Not Found
      Object.defineProperty(this, 'statusText', { value: 'Not Found', writable: true });
      Object.defineProperty(this, 'responseText', { value: '404 Not Found', writable: true });
      
      // Step 4: Trigger the appropriate callback to simulate request completion
      setTimeout(() => {
        if (typeof this.onerror === 'function') {
          // Trigger error handler if available (simulating network error)
          this.onerror(new Error('404 Not Found'));
        } else if (typeof this.onload === 'function') {
          // Fall back to onload with 404 status if no error handler
          this.onload();
        }
      }, 0);
      
      // Step 5: Return early to prevent the actual request
      return;
    }
 
    // For all other requests, proceed normally
    return originalXHRSend.apply(this, arguments);
  };
}

require("jquery-ui-sortable");
require("formBuilder");

/**
 * Main FormBuilder Component
 * Handles form creation, field management, and form submission
 * Features:
 * - Drag and drop form builder
 * - Custom field validation
 * - Template support
 * - Form preview
 * - Column visibility management
 */
const FormBuilderComponent = () => {
  const { setNotification } = useContext(LoginFormContext);
  const fb = useRef();
  let [formBuilder, setFormBuilder] = useState(null);
  let [fields, setFields] = useState([]);
  const { form, setForm } = useContext(FormBuilderContext);
  const [isBtnDisabled, setIsBtnDisabled] = useState(true);
  const [openPreview, setOpenPreview] = useState(false);
  const [error, setError] = useState(""); // For form error
  const [formBuilderUpdateCount, setFormBuilderUpdateCount] = useState(0);
  const [templateForms, setTemplateForms] = useState([]);
  const navigate = useNavigate();
  const [listColumns, setListColumns] = useState(CHILD_FORM_DEFAULT_COLUMNS);

  /**
   * FormBuilder Options Configuration
   * Configures the behavior and appearance of the form builder
   * - Controls field positioning
   * - Manages field buttons and actions
   * - Handles field edit events
   * - Configures field validation
   */
  const options = useCallback(() => {
    return {
      controlPosition: "left",
      controlOrder: ["text", "select", "textarea"],
      disabledActionButtons: ["data", "save", "clear"],
      disabledFieldButtons: {
        autocomplete: ["copy"],
        button: ["copy"],
        "checkbox-group": ["copy"],
        date: ["copy"],
        file: ["copy"],
        header: ["copy"],
        hidden: ["copy"],
        number: ["copy"],
        paragraph: ["copy"],
        "radio-group": ["copy"],
        select: ["copy"],
        starRating: ["copy"],
        text: ["copy"],
        textarea: ["copy"],
        checkbox: ["copy"],
      },
      async onOpenFieldEdit(editPanel) {
        const placeholderWrap = document.getElementsByClassName("placeholder-wrap");
        const nameWrap = document.getElementsByClassName("name-wrap");

        const formElements = document.getElementsByClassName("form-elements");

        for (let index = 0; index < placeholderWrap.length; index += 1) {
          placeholderWrap[index].children[0].innerHTML = "Display Text";
        }

        // Disable the name fields on option open
        for (let index = 0; index < nameWrap.length; index += 1) {
          // @ts-ignore
          nameWrap[index].children[1].children[0].disabled = true;
        }

        for (let index = 0; index < formElements.length; index += 1) {
          const createMinLengthDiv = document.createElement("div");
          createMinLengthDiv.className = "form-group minlength-wrap";

          const createLabel = document.createElement("label");
          createLabel.innerHTML = "Min Length";

          const createInputWrapDiv = document.createElement("div");
          createInputWrapDiv.className = "input-wrap";

          const createInput = document.createElement("input");
          createInput.className = "form-control";
          createInput.name = "minlength";
          createInput.style.width = "75px";
          createInput.type = "number";

          createMinLengthDiv.appendChild(createLabel);
          createInputWrapDiv.appendChild(createInput);
          createMinLengthDiv.appendChild(createInputWrapDiv);

          let isMinLengthElementPushed = false;

          for (let j = 0; j < formElements[index].children.length; j += 1) {
            if (formElements[index].children[j].className === "form-group minlength-wrap") {
              isMinLengthElementPushed = true;
            }
          }
          if (!isMinLengthElementPushed) {
            formElements[index].appendChild(createMinLengthDiv);
          }
        }

        // Safely get field label with null checks
        const getFieldLabel = () => {
          try {
            // Check if the DOM structure exists before accessing
            if (editPanel?.children?.[0]?.children?.[1]?.children?.[1]?.innerText) {
              return editPanel.children[0].children[1].children[1].innerText;
            }
            return null;
          } catch (error) {
            console.warn("Could not access field label:", error);
            return null;
          }
        };
        
        const fieldLabel = getFieldLabel();
        
        // Field-specific logic for Email, Service, Date, and special fields
        if (fieldLabel === "Email") {
          const emailChildren = editPanel.children[0].children;
          for (let index = 0; index < emailChildren.length; index += 1) {
            if (
              emailChildren[index]?.className?.includes("maxlength-wrap") ||
              emailChildren[index]?.className?.includes("minlength-wrap")
            ) {
              emailChildren[index].remove();
            }
          }
        } else if (fieldLabel === "Service") {
          const serviceChildren = editPanel.children[0].children;
          for (let index = 0; index < serviceChildren.length; index += 1) {
            if (serviceChildren[index]?.className?.includes("other-wrap")) {
              serviceChildren[index].remove();
            }
          }
        } else if (fieldLabel && fieldLabel.toLowerCase().includes("date")) {
          const dateChildren = editPanel.children[0].children;
          for (let index = 0; index < dateChildren.length; index += 1) {
            if (
              dateChildren[index]?.className?.includes("maxlength-wrap") ||
              dateChildren[index]?.className?.includes("minlength-wrap") ||
              dateChildren[index]?.className?.includes("min-wrap") ||
              dateChildren[index]?.className?.includes("max-wrap") ||
              dateChildren[index]?.className?.includes("step-wrap")
            ) {
              dateChildren[index].remove();
            }
            if (dateChildren[index]?.className?.includes("max-wrap")) {
              dateChildren[index].remove();
            }
            if (dateChildren[index]?.className?.includes("step-wrap")) {
              dateChildren[index].remove();
            }
          }
        } else if (
          fieldLabel && [
            "Court Action",
            "Court Documents",
            "Adoption",
            "Appointment Appearance",
            "Appointment Timeliness",
            "Calculate Hours In Weeks Or Months",
            "Case Transfer Family Relocated",
            "Case Treatment Plan",
            "Change In Placement",
            "Compliant",
            "Did Family Services/Child Welfare Agency Remove?",
            "Engaged",
            "Face To Face",
            "Family Refused Non Compliant",
            "Family Services/Child Welfare Agency Safety Plan",
            "Family Unable To Contact Non Engaged",
            "Gender",
            "Guardianship Established",
            "Inappropriate Case",
            "Is Case Treatment Mandated?",
            "Is Case Treatment Provided By Private Payor?",
            "Kinship/Non-Kinship Foster",
            "Legal Status (Is The Court Involved?) Of Abuser",
            "Location Of Service",
            "Preparedness Before Appointment",
            "Reunification",
            "Was Non-Custodial Parent(S) And Child(Ren) Information Provided?",
          ].includes(fieldLabel)
        ) {
          // Remove Other option from the radio and checkbox type fields
          const optionChildren = editPanel.children[0].children;
          for (let index = 0; index < optionChildren.length; index += 1) {
            if (optionChildren[index]?.className?.includes("other-wrap")) {
              optionChildren[index].remove();
            }
          }
        }
        if (fieldLabel && ["Is Case Treatment Mandated?"].includes(fieldLabel)) {
          // Remove Other option from this field
          const optionChildren = editPanel.children[0].children;
          for (let index = 0; index < optionChildren.length; index += 1) {
            if (optionChildren[index]?.className?.includes("other-wrap")) {
              optionChildren[index].remove();
            }
          }
        }
      },
      onAddField(fieldId) {
        if (isBtnDisabled) {
          setIsBtnDisabled(false);
        }

        const splittedFieldId = fieldId.split("-");
        splittedFieldId[splittedFieldId.length - 1] = `${
          +splittedFieldId[splittedFieldId.length - 1] + 1
        }`;

        const finalFieldId = splittedFieldId.join("-");

        setTimeout(() => {
          const test = document.getElementById(finalFieldId);

          if (
            test?.children?.[1]?.innerText === CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA ||
            test?.children?.[1]?.innerText === CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD
          ) {
            const textArea = test.children[5].children[0];
            const textAreaChildren = textArea.children;

            const createMinLengthDiv = document.createElement("div");
            createMinLengthDiv.className = "form-group displayLabel-wrap";

            const createLabel = document.createElement("label");
            createLabel.innerHTML = "Display Label";

            const createInputWrapDiv = document.createElement("div");
            createInputWrapDiv.className = "input-wrap";

            const createInput = document.createElement("input");
            createInput.className = "form-control";
            createInput.name = "displayLabel";
            createInput.style.width = "100%";
            createInput.type = "text";
            createInput.required = true;

            let nameInput = null;

            for (let j = 0; j < textAreaChildren.length; j += 1) {
              if (textAreaChildren[j].className === "form-group name-wrap") {
                nameInput = textAreaChildren[j].children[1].children[0];
              }
            }

            createInput.addEventListener("input", (event) => {
              const input = event.target;

              // Allow most characters for display label
              // Only restrict characters that could cause issues
              const sanitizedDisplayLabel = input.value.replace(/[^a-zA-Z0-9 ]/g, "");
              input.value = sanitizedDisplayLabel;

              // Generate valid variable name for internal use
              const validVariableName =
                sanitizedDisplayLabel
                  // Convert to lowercase
                  .toLowerCase()
                  // Replace spaces and special chars with underscore
                  .replace(/[^a-z0-9]+/g, "_")
                  // Remove leading numbers/special chars
                  .replace(/^[^a-z]+/, "")
                  // Remove trailing special chars
                  .replace(/_+$/, "") ||
                // Handle empty string
                nameInput?.value;

              if (nameInput) {
                nameInput.value = validVariableName;
              }
            });

            createMinLengthDiv.appendChild(createLabel);
            createInputWrapDiv.appendChild(createInput);
            createMinLengthDiv.appendChild(createInputWrapDiv);

            let isMinLengthElementPushed = false;

            for (let j = 0; j < textAreaChildren.length; j += 1) {
              if (textAreaChildren[j].className === "form-group displayLabel-wrap") {
                isMinLengthElementPushed = true;
              }
            }

            if (!isMinLengthElementPushed) {
              textArea.appendChild(createMinLengthDiv);
            }
          }

          const liTag = document.getElementsByClassName("input-control ui-sortable-handle");

          for (let index = 0; index < liTag.length; index += 1) {
            const currentFieldText = liTag[index]?.children?.[0]?.innerText || '';
            // Get only the field name from test.innerText by splitting on newlines
            const testFieldName = test?.innerText?.split('\n')?.[0]?.trim() || '';
            // Check if either the field names match exactly
            const isExactMatch = testFieldName === currentFieldText;

            // Disable field if it matches
            if (isExactMatch) {
              setError("");
              liTag[index].style.pointerEvents = "none";
              liTag[index].style.opacity = "0.6";
            }

            // Always keep custom fields enabled
            if (
              currentFieldText === CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA ||
              currentFieldText === CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD
            ) {
              liTag[index].style.pointerEvents = "all";
              liTag[index].style.opacity = "1";
            }
          }

          // Add event listeners for remove/move
          const removeBtn = document.getElementById(`del_${finalFieldId}`);
          const moveUpBtn = document.getElementById(`${finalFieldId}-sort-higher`);
          const moveDownBtn = document.getElementById(`${finalFieldId}-sort-lower`);

          removeBtn.addEventListener("click", (event) => {
            for (let index = 0; index < liTag.length; index += 1) {
              const currentFieldText = liTag[index]?.children?.[0]?.innerText || '';
              const testText = test?.innerText || '';
              if (
                currentFieldText.includes(testText) ||
                testText.split("\n").includes(currentFieldText)
              ) {
                liTag[index].style.pointerEvents = "all";
                liTag[index].style.opacity = "1";
              }
            }

            setTimeout(() => {
              setFormBuilderUpdateCount((prev) => prev + 1);
            }, 300);
          });

          setTimeout(() => {
            setFormBuilderUpdateCount((prev) => prev + 1);
          }, 100);
        }, 100);
      },
      onCloseFieldEdit(editPanel) {
        const divOfInputField = document.getElementsByClassName("formbuilder-text form-group");
        const divOfTextAreaField = document.getElementsByClassName(
          "formbuilder-textarea form-group"
        );
        for (let index = 0; index < divOfInputField.length; index += 1) {
          divOfInputField[index].children[1].style.width = "100%";
        }

        for (let index = 0; index < divOfTextAreaField.length; index += 1) {
          divOfTextAreaField[index].children[1].style.width = "100%";
        }
      },
      disableFields: [
        "autocomplete",
        "button",
        "checkbox-group",
        "date",
        "file",
        "header",
        "hidden",
        "number",
        "paragraph",
        "radio-group",
        "select",
        "starRating",
        // 'text',
        // 'textarea',
      ],
      i18n: {
        override: {
          "en-US": {
            text: CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD,
            textArea: CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA,
          },
        },
      },
    };
  }, []);

  /**
   * Fetches form elements from the API
   * Retrieves available form fields and their configurations
   */
  const handleFormElementListing = useCallback(async () => {
    try {
      const response = await getFormElements();
      if (response?.status === 200) {
        setFields(response.data.data);
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response?.data?.message || "Failed to fetch form elements",
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: error?.response?.data?.message || "Failed to fetch form elements",
        open: true,
      });
    }
  }, [setNotification]);

  /**
   * Fetches all template forms
   * Gets existing form templates that can be used as base for new forms
   */
  const handleGetAllTemplateForms = useCallback(async () => {
    try {
      const response = await getAllTemplateFormsApi();
      if (response?.status === 200) {
        const data = response.data.data.rows;
        const templateData =
          data
            ?.map((template) => ({
              ...template,
              id: template["id"],
              label: template["name"],
            }))
            ?.sort((a, b) => a.label.localeCompare(b.label)) || [];
        setTemplateForms(templateData);
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response?.data?.message || "Failed to fetch template forms",
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: error?.response?.data?.message || "Failed to fetch template forms",
        open: true,
      });
    }
  }, [setNotification]);

  /**
   * Initialize form elements and template forms
   * Fetches initial data when component mounts
   */
  useEffect(() => {
    handleFormElementListing();
    handleGetAllTemplateForms();
  }, [handleFormElementListing, handleGetAllTemplateForms]);

  /**
   * Initialize form builder
   * Sets up jQuery form builder with configured options
   * - Initializes drag and drop functionality
   * - Sorts form elements alphabetically
   * - Adds tooltips to elements
   */
  useEffect(() => {
    if (fb.current) {
      $(fb.current)
        .formBuilder({
          ...options(),
          fields,
        })
        .promise.then((formBuilder) => {
          setFormBuilder(formBuilder);

          // Sort form elements alphabetically
          const items = $(".frmb-control.ui-sortable > li").get();
          items.sort(function (a, b) {
            const keyA = $(a).text();
            const keyB = $(b).text();

            if (keyA < keyB) return -1;
            if (keyA > keyB) return 1;
            return 0;
          });
          const ul = $(".frmb-control.ui-sortable");
          $.each(items, function (i, li) {
            ul.append(li);
          });

          // Add tooltip for every li tag
          items.forEach((item) => {
            if (item?.innerText) {
              item.title = item.innerText;
            }
          });
        });

      setTimeout(() => {
        const dragArea = document.getElementsByClassName("frmb stage-wrap ui-sortable empty");
        if (dragArea.item(0)) {
          dragArea.item(0).dataset.content = DRAG_AREA_TEXT;
        }
      }, 100);
    }
  }, [fields, options]);

  // Search functionality - will be moved after formik initialization

  async function saveData() {
    const minLength = document.getElementsByName("minlength");
    const displayLabel = document.getElementsByName("displayLabel");

    const parsedDataInstance = JSON.parse(formBuilder.formData);

    if (!parsedDataInstance.length) {
      setError("Please add at least one field to the form");
      return;
    }

    // Check for empty display label
    let isEmpty = false;
    displayLabel.forEach((input) => {
      if (input.value.trim() === "") {
        isEmpty = true;
      }
    });
    if (isEmpty) {
      setError("Display labels cannot be empty. Please provide a display label for all fields.");
      return;
    }

    // Check for duplicate field names
    const fieldNames = parsedDataInstance.map((field) => field.name);

    // Check for empty field names
    const hasEmptyNames = fieldNames.some((name) => !name.trim());
    if (hasEmptyNames) {
      setError("Field names cannot be empty. Please provide a name for all fields.");
      return;
    }

    const hasDuplicates = fieldNames.some((name, index) => fieldNames.indexOf(name) !== index);
    if (hasDuplicates) {
      setError("Duplicate field names found. Each field must have a unique name.");
      return;
    }

    // Process min length values
    for (let index = 0; index < minLength.length; index++) {
      for (let j = 0; j < parsedDataInstance.length; j++) {
        if (index === j && parsedDataInstance[j].type !== "select") {
          parsedDataInstance[j] = {
            ...parsedDataInstance[j],
            minlength: +minLength[index].value > 0 ? +minLength[index].value : undefined,
          };
        }
      }
    }

    // Process display labels for custom fields
    let processedCount = 0;
    for (let index = 0; index < displayLabel.length; index++) {
      const j = parsedDataInstance.findIndex((item, idx) => {
        const isMatch =
          (item?.label === CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA && item?.type === "textarea") ||
          (item?.label === CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD && item?.type === "text");
        const isAfterProcessed = idx >= processedCount;
        return isMatch && isAfterProcessed;
      });

      if (j !== -1) {
        parsedDataInstance[j] = {
          ...parsedDataInstance[j],
          display_label: displayLabel[index].value?.trim() || "",
          is_custom: true,
        };
        processedCount = j + 1;
      }
    }

    // Add sort index and field_id
    parsedDataInstance.forEach((element, index) => {
      const matchingField = fields?.find((field) => element.name === field.name);
      element["sort"] = index + 1;
      element["field_id"] = matchingField ? +matchingField.id : null;
    });

    setError("");
    setForm(parsedDataInstance);
  }

  function clearData() {
    formBuilder.actions.clearFields();
    setForm({});

    const liTag = document.getElementsByClassName("input-control ui-sortable-handle");
    for (let index = 0; index < liTag.length; index++) {
      liTag[index].style.pointerEvents = "all";
      liTag[index].style.opacity = "1";
    }

    setTimeout(() => {
      const dragArea = document.getElementsByClassName(
        "frmb stage-wrap pull-right ui-sortable empty"
      );
      if (dragArea.item(0)) {
        dragArea.item(0).dataset.content = DRAG_AREA_TEXT;
      }
    }, 100);
  }

  // Define schema based on type
  const whenTypeIsOne = Yup.object().shape({
    name: Yup.string().trim().required("Form Name is required"),
    type: Yup.object().required("Type is required"),
    prefix: Yup.string()
      .required("Prefix is required")
      .matches(/^[a-zA-Z]{2,3}$/, "Prefix must contain min. 2 to max. 3 alphabetic characters"),
  });

  const whenTypeIsNotOne = Yup.object().shape({
    name: Yup.string().trim().required("Form Name is required"),
    type: Yup.object().required("Type is required"),
    prefix: Yup.string()
      .required("Prefix is required")
      .matches(/^[a-zA-Z]{2,3}$/, "Prefix must contain min. 2 to max. 3 alphabetic characters"),
  });

  const FormBuilderSchema = Yup.lazy((values) =>
    values.type && values.type.id === 1 ? whenTypeIsOne : whenTypeIsNotOne
  );

  const formik = useFormik({
    initialValues: {
      name: "",
      type: { id: 1, label: "Form" },
      template: [],
      applicable_to_all_services: false,
      search_elements: null,
      prefix: "",
      list_columns: CHILD_FORM_DEFAULT_COLUMNS,
    },
    validationSchema: FormBuilderSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setSubmitting(true);

        if (!form.length) {
          setNotification({
            color: "error",
            icon: "cancel",
            title: "Error",
            content: "Please select some fields to create a form",
            open: true,
          });
          return;
        }

        if (error) {
          setSubmitting(false);
          return;
        }

        const formBuilderData = {
          name: values.name,
          form_type: values.type.id,
          template: values.template.map((template) => +template.id), // Only pass template IDs
          prefix: values.prefix,
          form,
          visible_fields:
            values.type.id === 2 ? undefined : values?.list_columns || CHILD_FORM_DEFAULT_COLUMNS,
        };

        values = { ...values, form: form };
        const response = await createForm(formBuilderData);

        if (response && response.status === 200) {
          setSubmitting(false);
          resetForm();
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: response.data.message,
            open: true,
          });
          navigate("/form-builder");
        } else {
          setSubmitting(false);
          setNotification({
            color: "error",
            icon: "cancel",
            title: "Error",
            content: response.data.message,
            open: true,
          });
        }
      } catch (error) {
        setSubmitting(false);
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: error.message || "Something went wrong!",
          open: true,
        });
      }
    },
  });

  useEffect(() => {
    const items = $(".frmb-control.ui-sortable > li").get();
    const ul = $(".frmb-control.ui-sortable");
    let hasMatches = false;

    for (let i = 0; i < items.length; i++) {
      const txtValue = items[i]?.textContent || items[i]?.innerText || '';
      if (txtValue.toUpperCase().includes(formik.values.search_elements?.toUpperCase())) {
        items[i].style.display = "";
        hasMatches = true;
      } else {
        items[i].style.display = "none";
      }
    }

    // Check if there is a "No Matches Found" item and remove it if necessary
    const noMatchesItem = document.querySelector(".no-matches");
    if (noMatchesItem) {
      $(".no-matches").remove();
    }

    // If no matches, add the "No Matches Found" item
    if (!hasMatches) {
      const noMatchesLi = document.createElement("li");
      noMatchesLi.textContent = "No Matches Found";
      noMatchesLi.className = "no-matches";
      ul.append(noMatchesLi);
    }
  }, [formik.values.search_elements]);

  /**
   * Column management effect
   * Handles dynamic updates to form columns
   * - Updates columns when form fields change
   * - Maintains column order and visibility
   * - Ensures mandatory columns are present
   */
  useMemo(() => {
    if (!formBuilder || formik.values.type?.id === 2) return;

    // Parse the current form builder data
    const minLength = document.getElementsByName("minlength");
    const displayLabel = document.getElementsByName("displayLabel");
    const parsedDataInstance = formBuilder?.formData ? JSON.parse(formBuilder?.formData) : [];

    // Process minLength and displayLabel for custom fields
    for (let index = 0; index < minLength.length; index++) {
      for (let j = 0; j < parsedDataInstance.length; j++) {
        if (index === j && parsedDataInstance[j].type !== "select") {
          parsedDataInstance[j] = {
            ...parsedDataInstance[j],
            minlength: +minLength[index].value > 0 ? +minLength[index].value : undefined,
          };
        }
      }
    }
    let processedCount = 0;
    for (let index = 0; index < displayLabel.length; index++) {
      const j = parsedDataInstance.findIndex((item, idx) => {
        const isMatch =
          (item?.label === CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA && item?.type === "textarea") ||
          (item?.label === CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD && item?.type === "text");
        const isAfterProcessed = idx >= processedCount;
        return isMatch && isAfterProcessed;
      });

      if (j !== -1) {
        parsedDataInstance[j] = {
          ...parsedDataInstance[j],
          display_label: displayLabel[index].value?.trim() || "",
          is_custom: true,
        };
        processedCount = j + 1;
      }
    }
    parsedDataInstance.forEach((element, index) => {
      const matchingField = fields?.find((field) => element.name === field.name);
      element["sort"] = index + 1;
      element["field_id"] = matchingField ? +matchingField.id : null;
    });

    // Column reactivity logic
    const fData = parsedDataInstance;
    const lColumn = listColumns || [];
    const fDataIds = new Set([
      ...(fData?.map((f) => f.name) || []),
      ...(CHILD_FORM_DEFAULT_COLUMNS?.map((f) => f.id) || []),
    ]);
    const prevIds = new Set(lColumn.map((f) => f.id));

    // Add new fields from fData that are not in prev
    const toAdd = fData
      .filter((f) => !prevIds.has(f.name))
      ?.map((field) => ({
        id: field.name,
        label: field.display_label || field.label,
        type: field.type,
        index: field?.sort + 5,
        show: true,
        disableSort: false,
        disableFilter: false,
      }));

    // Keep only fields in prev that are still in fData (this removes orphaned fields)
    const toKeep = lColumn.filter((f) => fDataIds.has(f.id));
    const hasChanges = toAdd?.length > 0 || toKeep.length !== lColumn.length;

    if (!hasChanges) return;

    // Always ensure action column is last
    const columnsWithoutAction = [...toKeep, ...toAdd].filter((col) => col.id !== "action");
    const actionColumn = CHILD_FORM_DEFAULT_COLUMNS.find((col) => col.id === "action");

    if (actionColumn) {
      const maxIndex = Math.max(...columnsWithoutAction.map((col) => col.index || 0));
      const updatedActionColumn = {
        ...actionColumn,
        index: maxIndex + 1,
      };
      formik.setFieldValue("list_columns", [...columnsWithoutAction, updatedActionColumn]);
      setListColumns([...columnsWithoutAction, updatedActionColumn]);
    } else {
      formik.setFieldValue("list_columns", columnsWithoutAction);
      setListColumns(columnsWithoutAction);
    }
  }, [formBuilderUpdateCount, formBuilder, fields, listColumns, formik.values.type]);

  /**
   * Updates column visibility
   * Manages which columns are visible in the form table view
   */
  const handleColumnChange = (id, show) => {
    const updatedColumns = listColumns.map((item) => {
      if (item.id === id) {
        return { ...item, show };
      }
      return item;
    });
    setListColumns(updatedColumns);
    formik.setFieldValue("list_columns", updatedColumns);
  };

  /**
   * Gets parsed form data
   * Processes and formats form data for preview and submission
   * - Handles min length values
   * - Processes custom field display labels
   * - Adds sort order and field IDs
   */
  const getParsedFormData = () => {
    const minLength = document.getElementsByName("minlength");
    const displayLabel = document.getElementsByName("displayLabel");

    const parsedDataInstance = formBuilder?.formData ? JSON.parse(formBuilder?.formData) : [];

    for (let index = 0; index < minLength.length; index++) {
      for (let j = 0; j < parsedDataInstance.length; j++) {
        if (index === j && parsedDataInstance[j].type !== "select") {
          parsedDataInstance[j] = {
            ...parsedDataInstance[j],
            minlength: +minLength[index].value > 0 ? +minLength[index].value : undefined,
          };
        }
      }
    }

    let processedCount = 0;
    for (let index = 0; index < displayLabel.length; index++) {
      const j = parsedDataInstance.findIndex((item, idx) => {
        const isMatch =
          (item?.label === CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA && item?.type === "textarea") ||
          (item?.label === CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD && item?.type === "text");
        const isAfterProcessed = idx >= processedCount;
        return isMatch && isAfterProcessed;
      });

      if (j !== -1) {
        parsedDataInstance[j] = {
          ...parsedDataInstance[j],
          display_label: displayLabel[index].value?.trim() || "",
          is_custom: true,
        };
        processedCount = j + 1;
      }
    }

    parsedDataInstance.forEach((element, index) => {
      const matchingField = fields?.find((field) => element.name === field.name);
      element["sort"] = index + 1;
      element["field_id"] = matchingField ? +matchingField.id : null;
    });

    return parsedDataInstance;
  };

  return (
    <>
      {fields?.length > 0 ? (
        <>
          {/* Form Builder Interface */}
          <form onSubmit={formik.handleSubmit} noValidate>
            {/* Form Header Section - Name, Type, Prefix */}
            <MDBox
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "row",
                gap: 1,
              }}
            >
              {/* Form name, type and prefix inputs */}
              <TextField
                type="text"
                value={formik.values.name}
                error={Boolean(formik.touched.name && formik.errors.name)}
                // errorMessage={formik.touched.name && formik.errors.name}
                fullWidth
                label="Form Name"
                name="name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                required
              />
              <MDAutocomplete
                value={formik.values.type}
                sx={{
                  p: 0,
                  maxHeight: "40px",
                  "& .MuiInputBase-root": {
                    p: "4px",
                  },
                }}
                fullWidth
                onChange={(event, newValue) => {
                  formik.setFieldValue("type", newValue);
                }}
                options={[
                  { id: 1, label: "Form" },
                  { id: 2, label: "Template" },
                ]}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Type"
                    sx={{ p: 0 }}
                    error={Boolean(formik.touched.type && formik.errors.type)}
                    helperText={formik.touched.type && formik.errors.type}
                    required
                  />
                )}
              />
              <TextField
                type="text"
                value={formik.values.prefix}
                error={Boolean(formik.touched.prefix && formik.errors.prefix)}
                helperText={formik.touched.prefix && formik.errors.prefix}
                fullWidth
                label="Prefix"
                name="prefix"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                required
              />
            </MDBox>

            {/* Search and Preview Section */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              {/* Add search input above the form builder area */}
              <TextField
                type="text"
                value={formik.values.search_elements || ""}
                fullWidth
                sx={{ maxWidth: 300 }}
                label="Search Fields"
                name="search_elements"
                onChange={formik.handleChange}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => {
                        formik.setFieldValue("search_elements", "");
                      }}
                    >
                      <Icon>clear</Icon>
                    </IconButton>
                  ),
                }}
              />
              {formBuilder?.formData && JSON.parse(formBuilder?.formData).length > 0 && (
                <MDButton
                  color="primary"
                  size="small"
                  variant="contained"
                  onClick={() => setOpenPreview(true)}
                >
                  Preview
                </MDButton>
              )}
            </Box>

            {/* Form Builder Area */}
            <Box ref={fb} className="form-builder-area" sx={{ mb: 3 }} />

            {/* Template Forms Section */}
            {formik.values.type?.id === 1 && (
              <Grid>
                <MDTypography variant="h5" sx={{ mt: 5, mb: 2 }}>
                  Template Forms
                </MDTypography>
                {templateForms.length === 0 ? (
                  <MDTypography variant="caption" color="warning.main" sx={{ mt: 1 }}>
                    No templates available
                  </MDTypography>
                ) : (
                  <Grid container spacing={2}>
                    {templateForms.map((template) => (
                      <Grid item xs={12} sm={4} key={template.id}>
                        <Checkbox
                          checked={formik.values.template.some((t) => t.id === template.id)}
                          onChange={(event) => {
                            const isChecked = event.target.checked;
                            const currentTemplates = formik.values.template;
                            if (isChecked) {
                              formik.setFieldValue("template", [...currentTemplates, template]);
                            } else {
                              formik.setFieldValue(
                                "template",
                                currentTemplates.filter((t) => t.id !== template.id)
                              );
                            }
                          }}
                          inputProps={{ "aria-label": template.label }}
                        />
                        <MDTypography
                          component="span"
                          sx={{ ml: 1, verticalAlign: "middle", fontSize: "16px" }}
                        >
                          {template.label}
                        </MDTypography>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            )}

            {/* Visible Columns Configuration */}
            {formik.values.type?.id !== 2 && (
              <Grid sx={{ my: 2 }}>
                <MDTypography variant="h5" sx={{ mb: 2 }}>
                  Visible Columns
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Toggle which columns are visible in the case form table. Mandatory columns cannot
                  be hidden.
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {listColumns?.map((item) => {
                    const isMandatory = CHILD_FORM_MANDATORY_COLUMNS.includes(item.id);
                    return (
                      <Grid item xs={12} sm={4} md={4} key={item.id}>
                        <Checkbox
                          checked={item.show}
                          onChange={() => !isMandatory && handleColumnChange(item.id, !item.show)}
                          disabled={isMandatory}
                          inputProps={{ "aria-label": item.label }}
                          sx={{
                            "&.Mui-disabled": {
                              opacity: 0.5,
                              cursor: "not-allowed",
                            },
                          }}
                        />
                        <MDTypography
                          component="span"
                          sx={{ ml: 1, verticalAlign: "middle", fontSize: "16px" }}
                        >
                          {item.label}
                        </MDTypography>
                      </Grid>
                    );
                  })}
                </Grid>
                {listColumns?.filter((c) => c.show && !CHILD_FORM_MANDATORY_COLUMNS.includes(c.id))
                  .length === 0 && (
                  <MDTypography variant="caption" color="warning.main" sx={{ mt: 1 }}>
                    Only mandatory columns are currently visible.
                  </MDTypography>
                )}
              </Grid>
            )}

            {/* Error Display */}
            {!!error && (
              <FormHelperText error={!!error}>
                {error ?? "Please add at least one field to the form"}
              </FormHelperText>
            )}

            {/* Action Buttons */}
            <Box className="button-group" sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                onClick={saveData}
                disabled={formik.isSubmitting}
              >
                Create Form
              </Button>
              <MDLink to="/form-builder">
                <MDButton variant="outlined" color="primary">
                  Cancel
                </MDButton>
              </MDLink>
            </Box>
          </form>

          {/* Form Preview Dialog */}
          <DraggableDialog
            dialogTitle="Form Preview"
            open={openPreview}
            onClose={() => setOpenPreview(false)}
            dialogContent={
              <ViewFormPreview
                defaultFieldData={formBuilder?.formData ? JSON.parse(formBuilder?.formData) : []}
                watchTemplate={formik.values.type?.id === 1 ? formik.values.template : []}
              />
            }
            maxWidth="xl"
          />
        </>
      ) : (
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress color="success" />
          </Box>
        </Container>
      )}
    </>
  );
};

export default FormBuilderComponent;
